import React, { useState } from 'react';
import {
  View,
  Text,
  TextInput,
  StyleSheet,
  StatusBar,
  KeyboardAvoidingView,
  Platform,
  TouchableOpacity,
  Alert,
  ScrollView,
  Image,
} from 'react-native';
import Icon from '../components/Icon';
import { authAPI } from '../services/authApi';
import { useTheme } from '../context/ThemeContext';
import { ROUTES } from '../constants/routes';

const LoginScreen = ({ navigation }) => {
  const { colors, isDarkMode } = useTheme();
  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [selectedServer, setSelectedServer] = useState('fizmoFXMarket-Server');
  const [showServerDropdown, setShowServerDropdown] = useState(false);
  const [savePassword, setSavePassword] = useState(true);



  const servers = [
    "fizmoFXMarket-Server",
    "Demo-Server",
    "Live-Server",
    "Test-Server"
  ];

  const handleLogin = async () => {
    if (!login || !password) {
      Alert.alert('Error', 'Please enter login and password');
      return;
    }

    setIsLoading(true);
    
    try {
      
      const response = await authAPI.login({ 
        email: login.trim(), 
        password: password.trim(),
        serverCategory: selectedServer
      });
      
      console.log('Login response received:', response);
      
      // Response may come in different shapes depending on API and interceptors
      // Try common token locations
      const token = response?.token || response?.data?.token || response?.accessToken || response?.access_token || response?.authToken;

      if (token) {
        await authAPI.saveToken(token);
        // Save selected server
        await authAPI.saveSelectedServer(selectedServer);

        // Navigate immediately on web, use Alert on mobile
        if (Platform.OS === 'web') {
          console.log('Navigating to home screen...');
          navigation.replace(ROUTES.MAIN_TABS);
        } else {
          Alert.alert('Success', 'Login successful!', [
            {
              text: 'OK',
              onPress: () => navigation.replace(ROUTES.MAIN_TABS)
            }
          ]);
        }
      } else {
        // If API returned an error message inside the response body, show it
        const serverMessage = response?.message || response?.data?.message || response?.error || null;
        console.warn('Login response did not contain token:', response);
        Alert.alert('Error', serverMessage || 'Invalid response from server');
      }
    } catch (error) {
      console.error('Login error details:', {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        url: error.config?.url,
        code: error.code
      });
      
      let errorMessage = 'Login failed. Please check your credentials and try again.';
      
      if (error.code === 'NETWORK_ERROR' || error.message.includes('Network Error')) {
        errorMessage = 'Network error. Please check your internet connection and try again.';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.response?.status === 400) {
        errorMessage = 'Invalid email or password format';
      } else if (error.response?.status === 404) {
        errorMessage = 'Login service not available. Please contact support.';
      } else if (error.response?.status === 500) {
        errorMessage = 'Server error. Please try again later.';
      } else if (error.message && !error.message.includes('Network')) {
        errorMessage = error.message;
      }
      
      Alert.alert('Login Failed', errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <View style={[styles.container, { backgroundColor: colors.background }]}>
      <StatusBar barStyle={isDarkMode ? "light-content" : "dark-content"} backgroundColor={colors.background} />

      {/* Header */}
      <View style={[styles.header, { backgroundColor: colors.background }]}>
        <Image 
          source={require('../assets/trading_logo.jpeg')} 
          style={styles.headerLogo}
          resizeMode="contain"
        />
      </View>

      <ScrollView style={styles.scrollView} showsVerticalScrollIndicator={false}>
        {/* Demo Account Option */}
        <TouchableOpacity style={[styles.accountOption, { borderBottomColor: colors.border }]}>
          <Icon name="book-outline" size={20} color={colors.primary} style={styles.optionIcon} />
          <View style={styles.optionContent}>
            <Text style={[styles.optionTitle, { color: colors.text }]}>Open a demo account</Text>
            <Text style={[styles.optionSubtitle, { color: colors.textSecondary }]}>To learn trading and to test your strategies</Text>
          </View>
          <Icon name="chevron-forward" size={20} color={colors.textTertiary} />
        </TouchableOpacity>

        {/* Real Account Option */}
        <TouchableOpacity style={[styles.accountOption, { borderBottomColor: colors.border }]} onPress={() => navigation.navigate('Register')}>
          <Icon name="wallet-outline" size={20} color={colors.primary} style={styles.optionIcon} />
          <View style={styles.optionContent}>
            <Text style={[styles.optionTitle, { color: colors.text }]}>Open a real account</Text>
            <Text style={[styles.optionSubtitle, { color: colors.textSecondary }]}>For live trading, additional identification is required</Text>
          </View>
          <Icon name="chevron-forward" size={20} color={colors.textTertiary} />
        </TouchableOpacity>

        {/* Login Section */}
        <View style={[styles.loginSection, { borderBottomColor: colors.border }]}>
          <Text style={[styles.loginTitle, { color: colors.text }]}>Login to an existing account</Text>
        </View>

        <View style={styles.formContainer}>
          {/* EMAIL INPUT */}
          <View style={styles.inputWrapper}>
            <TextInput
              style={[styles.input, { color: colors.text, borderBottomColor: colors.border }]}
              placeholder="Email"
              placeholderTextColor={colors.textSecondary}
              value={login}
              onChangeText={setLogin}
              autoCapitalize="none"
              keyboardType="email-address"
            />
          </View>

          {/* PASSWORD INPUT */}
          <View style={styles.inputWrapper}>
            <TextInput
              style={[styles.input, { color: colors.text, borderBottomColor: colors.border }]}
              placeholder="Password"
              placeholderTextColor={colors.textSecondary}
              secureTextEntry={!showPassword}
              value={password}
              onChangeText={setPassword}
            />
          </View>

          {/* SERVER DROPDOWN */}
          <TouchableOpacity
            style={[styles.serverDropdown, { borderBottomColor: colors.border }]}
            onPress={() => setShowServerDropdown(!showServerDropdown)}
          >
            <Text style={[styles.serverLabel, { color: colors.textSecondary }]}>Server</Text>
            <View style={styles.serverValueContainer}>
              <Text style={[styles.serverValue, { color: colors.text }]}>{selectedServer}</Text>
              <Icon name="chevron-down" size={20} color={colors.textSecondary} />
            </View>
          </TouchableOpacity>

          {showServerDropdown && (
            <View style={[styles.dropdown, { backgroundColor: colors.cardBackground }]}>
              {servers.map((server, index) => (
                <TouchableOpacity
                  key={index}
                  style={[styles.dropdownItem, { borderBottomColor: colors.border }]}
                  onPress={() => {
                    setSelectedServer(server);
                    setShowServerDropdown(false);
                  }}
                >
                  <Text style={[styles.dropdownText, { color: colors.text }]}>{server}</Text>
                  {selectedServer === server && (
                    <Icon name="checkmark" size={20} color={colors.primary} />
                  )}
                </TouchableOpacity>
              ))}
            </View>
          )}

          {/* SAVE PASSWORD CHECKBOX */}
          <TouchableOpacity 
            style={styles.savePasswordContainer}
            onPress={() => setSavePassword(!savePassword)}
          >
            <Text style={[styles.savePasswordText, { color: colors.text }]}>Save password</Text>
            <View style={[styles.checkbox, { borderColor: colors.primary }, savePassword && { backgroundColor: colors.primary }]}>
              {savePassword && <Icon name="checkmark" size={16} color={isDarkMode ? "#FFFFFF" : "#FFFFFF"} />}
            </View>
          </TouchableOpacity>

          {/* FORGOT PASSWORD */}
          <TouchableOpacity
            style={styles.forgotPasswordContainer}
            onPress={() => navigation.navigate('ForgotPassword')}
          >
            <Text style={[styles.forgotPasswordText, { color: colors.primary }]}>Forgot password?</Text>
          </TouchableOpacity>

          {/* LOGIN BUTTON */}
          <TouchableOpacity
            style={[styles.loginButton, { backgroundColor: colors.primary }, isLoading && { opacity: 0.6 }]}
            onPress={handleLogin}
            disabled={isLoading}
          >
            <Text style={[styles.loginButtonText, { color: '#FFFFFF' }]}>
              {isLoading ? 'LOGGING IN...' : 'LOGIN'}
            </Text>
          </TouchableOpacity>




        </View>
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },

  header: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 16,
    paddingVertical: 16,
    paddingTop: 20,
  },

  headerLogo: {
    width: 200,
    height: 60,
  },

  headerTitle: {
    fontSize: 18,
    fontWeight: '600',
  },

  scrollView: {
    flex: 1,
  },

  accountOption: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 20,
    paddingHorizontal: 16,
    borderBottomWidth: 1,
  },

  optionIcon: {
    marginRight: 12,
  },

  optionContent: {
    flex: 1,
  },

  optionTitle: {
    fontSize: 16,
    fontWeight: '500',
    marginBottom: 4,
  },

  optionSubtitle: {
    fontSize: 13,
  },

  loginSection: {
    paddingVertical: 20,
    paddingHorizontal: 16,
    borderBottomWidth: 1,
  },

  loginTitle: {
    fontSize: 16,
    fontWeight: '600',
    textAlign: 'center',
  },

  formContainer: {
    paddingHorizontal: 16,
    paddingTop: 24,
  },

  inputWrapper: {
    marginBottom: 16,
  },

  input: {
    height: 48,
    backgroundColor: 'transparent',
    borderBottomWidth: 1,
    fontSize: 16,
    paddingHorizontal: 0,
  },

  serverDropdown: {
    paddingVertical: 12,
    borderBottomWidth: 1,
    marginBottom: 16,
  },

  serverLabel: {
    fontSize: 14,
    marginBottom: 4,
  },

  serverValueContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },

  serverValue: {
    fontSize: 16,
  },

  dropdown: {
    borderRadius: 8,
    marginBottom: 16,
    overflow: 'hidden',
  },

  dropdownItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 14,
    borderBottomWidth: 1,
  },

  dropdownText: {
    fontSize: 16,
  },

  savePasswordContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 8,
    marginBottom: 4,
  },

  savePasswordText: {
    fontSize: 16,
  },

  checkbox: {
    width: 24,
    height: 24,
    borderWidth: 2,
    borderRadius: 4,
    justifyContent: 'center',
    alignItems: 'center',
  },

  forgotPasswordContainer: {
    alignItems: 'center',
    paddingVertical: 8,
    marginBottom: 4,
  },

  forgotPasswordText: {
    fontSize: 15,
    fontWeight: '500',
  },

  loginButton: {
    height: 52,
    borderRadius: 8,
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 0,
  },

  loginButtonText: {
    fontSize: 16,
    fontWeight: '700',
    letterSpacing: 0.5,
  },


});

export default LoginScreen;
